import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { t } from 'i18next';
import Modal from '@mui/material/Modal';
import Box from '@mui/material/Box';
import { useSnackbar } from 'notistack';
import { modalBaseStyle } from '../util/modalStyle';

interface LeaveType { id: number; name: string; }

interface Props {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  userId: number;
  userName: string;
  year: number;
  types: LeaveType[];
  editing?: any | null;
  timeStep?: number;   // шаг минут (из company.hr_timeStepMinutes)
  embedded?: boolean;  // встроить в панель (без модального окна)
}

const KINDS = ['carryover', 'bonus', 'manual'];

const lbl: React.CSSProperties = {
  display: 'block', marginBottom: 4, fontSize: 11, fontWeight: 800,
  letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)',
};

const LeaveAdjustmentModal: React.FC<Props> = ({ open, onClose, onSaved, userId, userName, year, types, editing, timeStep = 15, embedded = false }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [typeId, setTypeId] = useState<number | ''>('');
  const [kind, setKind] = useState('carryover');
  const [direction, setDirection] = useState<'add' | 'deduct'>('add');
  const [hours, setHours] = useState('1');
  const [minutes, setMinutes] = useState('0');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setTypeId(editing.typeId);
      setKind(editing.kind === 'buy' || editing.kind === 'sell' ? 'manual' : (editing.kind ?? 'manual'));
      setDirection((editing.amount ?? 0) < 0 ? 'deduct' : 'add');
      const absHours = Math.abs(editing.amount ?? 0);
      const h = Math.floor(absHours);
      const m = Math.round((absHours - h) * 60);
      setHours(String(h));
      setMinutes(String(m));
      setReason(editing.reason ?? '');
    } else {
      setTypeId(types.find(Boolean)?.id ?? '');
      setKind('carryover');
      setDirection('add');
      setHours('1');
      setMinutes('0');
      setReason('');
    }
  }, [open, editing, types]);

  const save = async () => {
    if (typeId === '') { enqueueSnackbar(t('FillRequired') as string, { variant: 'warning' }); return; }
    const totalHours = (Number(hours) || 0) + (Number(minutes) || 0) / 60;
    if (totalHours <= 0) { enqueueSnackbar(t('FillRequired') as string, { variant: 'warning' }); return; }
    const signed = direction === 'deduct' ? -totalHours : totalHours;
    const body = { userId, typeId: Number(typeId), year, amount: Math.round(signed * 1000) / 1000, unit: 'hours', kind, reason: reason.trim() || null };
    setSaving(true);
    try {
      if (editing) await axios.put(`hr-leave-adjustment/${editing.id}`, body);
      else await axios.post('hr-leave-adjustment', body);
      enqueueSnackbar(t('Saved') as string, { variant: 'success' });
      onSaved();
      onClose();
    } catch {
      enqueueSnackbar(t('Error') as string, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const fields = (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-l2)' }}>
      {!embedded && (
        <div>
          <label style={lbl}>{t('Employee')}</label>
          <input className="timesheet-edit-input w-full" value={userName} disabled />
        </div>
      )}

      <div style={{ display: 'flex', gap: 'var(--space-l3)' }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>{t('HrAbsenceType')}</label>
          <select className="timesheet-edit-input w-full" value={typeId} onChange={e => setTypeId(Number(e.target.value))}>
            {types.map(tp => <option key={tp.id} value={tp.id}>{tp.name}</option>)}
          </select>
        </div>
        <div style={{ width: 90 }}>
          <label style={lbl}>{t('HrLeaveYear')}</label>
          <input className="timesheet-edit-input w-full" value={year} disabled />
        </div>
      </div>

      <div>
        <label style={lbl}>{t('HrDirection', 'Direction') as string}</label>
        <div style={{ display: 'flex', gap: 6 }}>
          <button type="button" onClick={() => setDirection('add')} className={`dt-btn-ghost${direction === 'add' ? ' active' : ''}`} style={{ fontSize: 12 }}>＋ {t('HrAdd', 'Add') as string}</button>
          <button type="button" onClick={() => setDirection('deduct')} className={`dt-btn-ghost${direction === 'deduct' ? ' active' : ''}`} style={{ fontSize: 12 }}>－ {t('HrDeduct', 'Deduct') as string}</button>
        </div>
      </div>

      <div>
        <label style={lbl}>{t('HrAdjKind')}</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {KINDS.map(k => (
            <button key={k} type="button" onClick={() => setKind(k)} className={`dt-btn-ghost${kind === k ? ' active' : ''}`} style={{ fontSize: 11 }}>{t('HrAdjKind_' + k)}</button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 'var(--space-l3)' }}>
        <div style={{ flex: 1 }}>
          <label style={lbl}>{t('HrHours', 'Hours') as string}</label>
          <input type="number" min="0" className="timesheet-edit-input w-full" value={hours} onChange={e => setHours(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={lbl}>{t('HrMinutes', 'Minutes') as string}</label>
          <input type="number" min="0" max="59" step={timeStep} className="timesheet-edit-input w-full" value={minutes} onChange={e => setMinutes(e.target.value)} />
        </div>
      </div>

      <div>
        <label style={lbl}>{t('HrReason')}</label>
        <textarea rows={2} className="timesheet-edit-input w-full" style={{ resize: 'none' }} value={reason} onChange={e => setReason(e.target.value)} />
      </div>
    </div>
  );

  const footer = (
    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-l3)' }}>
      <button type="button" className="dt-btn-ghost" onClick={onClose}>{t('Cancel')}</button>
      <button type="button" className="btn-primary" onClick={save} disabled={saving}>{t('Save')}</button>
    </div>
  );

  // Встроенный режим — форма прямо в панели (без модального окна)
  if (embedded) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <div className="thin-scroll" style={{ flex: 1, overflow: 'auto', padding: 16 }}>{fields}</div>
        <div style={{ padding: '12px 16px', borderTop: 'var(--border-input)', flexShrink: 0 }}>{footer}</div>
      </div>
    );
  }

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalBaseStyle}>
        <h3 style={{ fontSize: 15, fontWeight: 800, color: 'var(--text-color)', marginBottom: 'var(--space-l2)' }}>{t('HrAdjustment')}</h3>
        {fields}
        <div style={{ marginTop: 'var(--space-l2)' }}>{footer}</div>
      </Box>
    </Modal>
  );
};

export default LeaveAdjustmentModal;
